import React from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Instagram, MapPin, MessageCircle } from 'lucide-react';

export const Footer = () => {
  const footerLinks = {
    'Quick Links': [
      { name: 'Home', href: '/' },
      { name: 'Hosting Plans', href: '/hosting' },
      { name: 'WordPress', href: '/wordpress' },
      { name: 'VibeCoders', href: '/vibecoders' },
      { name: 'About Us', href: '/about' },
    ],
    'Hosting Services': [
      { name: 'Shared Hosting', href: '/hosting' },
      { name: 'WordPress Hosting', href: '/wordpress' },
      { name: 'Node.js Hosting', href: '/vibecoders' },
      { name: 'Python Hosting', href: '/vibecoders' },
    ],
    'Support': [
      { name: 'Contact Us', href: '/contact' },
      { name: 'Features', href: '/features' },
      { name: 'FAQ', href: '/features' },
      { name: 'Server Status', href: 'https://status.961host.net/', external: true },
    ],
  };

  return (
    <footer className="bg-foreground text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-8 lg:gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-2 space-y-6">
            <Link to="/">
              <img
                src="https://customer-assets.emergentagent.com/job_nextgen-hosting-2/artifacts/0arf8k4y_Complete%20Rectangular%20Logo%20-%20Black.png"
                alt="961Host"
                className="h-7 w-auto brightness-0 invert"
              />
            </Link>
            
            <p className="text-white/70 leading-relaxed max-w-sm">
              Premium web hosting solutions since 2007. Fast, reliable, and secure hosting for your websites with 24/7 expert support.
            </p>
            
            {/* Contact Info */}
            <div className="space-y-3">
              <Link 
                to="/contact" 
                className="flex items-center gap-3 text-white/70 hover:text-white transition-colors"
              >
                <MessageCircle className="w-4 h-4" />
                <span className="text-sm">Get in Touch</span>
              </Link>
              <div className="flex items-start gap-3 text-white/70">
                <MapPin className="w-4 h-4 mt-1" />
                <span className="text-sm">Beirut, Lebanon</span>
              </div>
            </div>
            
            {/* Social Links */}
            <div className="flex gap-3">
              <a
                href="https://facebook.com/961host"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-lg bg-white/10 border border-white/20 flex items-center justify-center hover:bg-white/20 transition-all"
                aria-label="Follow us on Facebook"
                data-testid="footer-facebook-link"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href="https://instagram.com/961host"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-lg bg-white/10 border border-white/20 flex items-center justify-center hover:bg-white/20 transition-all"
                aria-label="Follow us on Instagram"
                data-testid="footer-instagram-link"
              >
                <Instagram className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Links Columns */}
          {Object.entries(footerLinks).map(([title, links]) => (
            <div key={title}>
              <h4 className="font-semibold text-white mb-4">{title}</h4>
              <ul className="space-y-3">
                {links.map((link) => (
                  <li key={link.name}>
                    {link.external ? (
                      <a
                        href={link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-white/70 hover:text-white text-sm transition-colors"
                        data-testid={`footer-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        {link.name}
                      </a>
                    ) : (
                      <Link
                        to={link.href}
                        className="text-white/70 hover:text-white text-sm transition-colors"
                        data-testid={`footer-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        {link.name}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-white/10">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4 text-sm text-white/60">
            <p>© {new Date().getFullYear()} 961Host. All rights reserved.</p>
            <div className="flex gap-6">
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
